// frontend/src/components/ImagingPdfUpload.js
// Component for uploading and processing imaging PDFs with AI extraction

import React, { useState } from 'react';
import { Upload, Button, Alert, Spin, Card, Typography, Space, message } from 'antd';
import { UploadOutlined, FileTextOutlined, RobotOutlined } from '@ant-design/icons';
import moment from 'moment';
import MomentDatePicker from './MomentDatePicker.tsx';
import {
  processImagingPdf,
  validateImagingPdf,
  getImagingCategoryDisplayName,
} from '../services/imagingUploadService';
import ImagingDataReviewModal from './ImagingDataReviewModal';

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

const ImagingPdfUpload = ({ patientId, onSuccess, onCancel }) => {
  const [fileList, setFileList] = useState([]);
  const [studyDate, setStudyDate] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [tempFileInfo, setTempFileInfo] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Handle file selection
   */
  const handleFileChange = ({ fileList: newFileList }) => {
    // Only keep the last file
    const latestFile = newFileList.slice(-1);
    setFileList(latestFile);
    setError(null);
  };

  /**
   * Validate file before upload
   */
  const beforeUpload = (file) => {
    const validation = validateImagingPdf(file);
    
    if (!validation.isValid) {
      message.error(validation.error);
      return false;
    }

    return false; // Prevent automatic upload
  };

  /**
   * Handle PDF processing with AI
   */
  const handleProcessPdf = async () => {
    if (fileList.length === 0) {
      message.error('Please select a PDF file to upload');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const file = fileList[0].originFileObj;
      const studyDateStr = studyDate ? studyDate.format('YYYY-MM-DD') : null;

      console.log('[ImagingPdfUpload] Processing PDF:', file.name);

      const response = await processImagingPdf(patientId, file, studyDateStr);

      if (response.status === 'success') {
        console.log('[ImagingPdfUpload] AI extraction successful:', response.data);
        
        setExtractedData(response.data.extractedData);
        setTempFileInfo(response.data.tempFileInfo);
        setPatientInfo(response.data.patientInfo);
        setShowReviewModal(true);

        message.success('AI extraction completed! Please review the extracted data.');
      } else {
        throw new Error(response.message || 'Failed to process PDF');
      }
    } catch (err) {
      console.error('[ImagingPdfUpload] Error processing PDF:', err);
      
      const errorMessage = err.response?.data?.message || err.message || 'Failed to process imaging PDF';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  /**
   * Handle successful import from review modal
   */
  const handleImportSuccess = () => {
    setShowReviewModal(false);
    setFileList([]);
    setStudyDate(null);
    setExtractedData(null);
    setTempFileInfo(null);
    
    if (onSuccess) {
      onSuccess();
    }
  };

  /**
   * Handle cancel from review modal
   */
  const handleReviewCancel = () => {
    setShowReviewModal(false);
  };

  /**
   * Upload props configuration
   */
  const uploadProps = {
    name: 'imagingPdf',
    multiple: false,
    fileList: fileList,
    beforeUpload: beforeUpload,
    onChange: handleFileChange,
    onRemove: () => {
      setFileList([]);
      setError(null);
    },
    accept: '.pdf',
    maxCount: 1,
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Header */}
          <div>
            <Title level={4}>
              <RobotOutlined style={{ marginRight: 8 }} />
              AI-Powered Imaging Report Upload
            </Title>
            <Paragraph type="secondary">
              Upload a PDF imaging report and let AI automatically extract structured data.
              The system will identify the study type, extract findings, measurements, and interpretations.
            </Paragraph>
          </div>

          {/* Instructions */}
          <Alert
            message="How it works"
            description={
              <ol style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li>Upload a PDF imaging report (X-Ray, CT, MRI, Ultrasound, etc.)</li>
                <li>Optionally specify the study date</li>
                <li>Click "Process with AI" to extract data</li>
                <li>Review and edit the extracted data</li>
                <li>Confirm to save to patient flowsheet</li>
              </ol>
            }
            type="info"
            showIcon
          />

          {/* Study Date Selection */}
          <div>
            <Text strong>Study Date (Optional)</Text>
            <br />
            <MomentDatePicker
              value={studyDate}
              onChange={setStudyDate}
              format="YYYY-MM-DD"
              placeholder="Select study date"
              style={{ width: '100%', marginTop: 8 }}
              disabledDate={(current) => current && current > moment().endOf('day')}
              showToday
            />
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
              If not specified, the AI will attempt to extract the date from the PDF
            </Text>
          </div>

          {/* File Upload */}
          <div>
            <Text strong>Upload Imaging Report PDF</Text>
            <Dragger {...uploadProps} style={{ marginTop: 8 }}>
              <p className="ant-upload-drag-icon">
                <FileTextOutlined style={{ fontSize: 48, color: '#1890ff' }} />
              </p>
              <p className="ant-upload-text">Click or drag PDF file to this area</p>
              <p className="ant-upload-hint">
                Support for single PDF file upload. Maximum file size: 100MB
              </p>
            </Dragger>
          </div>

          {/* Error Display */}
          {error && (
            <Alert
              message="Processing Error"
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
            />
          )}

          {/* Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="primary"
              icon={<RobotOutlined />}
              onClick={handleProcessPdf}
              disabled={fileList.length === 0 || processing}
              loading={processing}
            >
              {processing ? 'Processing with AI...' : 'Process with AI'}
            </Button>
          </div>

          {/* Processing Indicator */}
          {processing && (
            <Card style={{ textAlign: 'center', backgroundColor: '#f0f5ff' }}>
              <Space direction="vertical" size="middle">
                <Spin size="large" />
                <div>
                  <Text strong>AI is analyzing your imaging report...</Text>
                  <br />
                  <Text type="secondary">
                    This may take 30-60 seconds. Please wait.
                  </Text>
                </div>
              </Space>
            </Card>
          )}
        </Space>
      </Card>

      {/* Review Modal */}
      {showReviewModal && extractedData && (
        <ImagingDataReviewModal
          visible={showReviewModal}
          patientId={patientId}
          extractedData={extractedData}
          tempFileInfo={tempFileInfo}
          patientInfo={patientInfo}
          initialStudyDate={studyDate ? studyDate.format('YYYY-MM-DD') : extractedData.studyDate}
          onSuccess={handleImportSuccess}
          onCancel={handleReviewCancel}
        />
      )}
    </div>
  );
};

export default ImagingPdfUpload;

